import { TailwindColor, tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { valueRender } from '@repo/toolkit/conditional-render'
import { DateFormatter } from '@repo/toolkit/date'
import Badge from '@repo/ui/badge'
import InnerBox from '@repo/ui/inner-box'
import Typography from '@repo/ui/typography'
import { useQuery } from '@tanstack/react-query'

import { getEnrollment } from '../../../../api/enrollments/get-enrollment'
import { getEnrollmentHealth } from '../../../../api/enrollments/get-enrollment-health'
import { enrollmentMedicalLevelFields } from '../../../../lib/types/fields/enrollments/enrollment'
import { enrollmentHealthMedicalStatementFields } from '../../../../lib/types/fields/enrollments/enrollment-health'
import LoadingScreen from '../../../elements/loading/loading-screen'
import { LabelValue } from './shared-client-space-ui'

type EnrollmentMedicalStatementProps = {
  enrollmentId: number
}

function LabelBadge({
  label,
  value,
  color
}: {
  label: string
  value?: string
  color?: TailwindColor
}) {
  return (
    <LabelValue label={label} color={color}>
      <Badge text={valueRender(value, (d) => DateFormatter.DDMMYYYY(d))} />
    </LabelValue>
  )
}

export function EnrollmentMedicalStatement({ enrollmentId }: EnrollmentMedicalStatementProps) {
  const { data: enrollment, isPending: isEnrollmentPending } = useQuery({
    queryKey: ['enrollment', enrollmentId, enrollmentMedicalLevelFields],
    queryFn: () => getEnrollment(enrollmentId, enrollmentMedicalLevelFields)
  })
  const { data: enrollmentHealth, isPending } = useQuery({
    queryKey: ['enrollment-health', enrollmentId, enrollmentHealthMedicalStatementFields],
    queryFn: () => getEnrollmentHealth(enrollmentId, enrollmentHealthMedicalStatementFields),
    enabled: !!enrollmentId
  })

  if (!enrollment || isEnrollmentPending || isPending) return <LoadingScreen />

  return (
    <div className={'flex flex-col gap-3'}>
      <InnerBox
        title={'Vaccins obligatoires'}
        className={'rounded-lg bg-white-hover border border-grey-light'}
      >
        <div className={'grid mobile:grid-cols-1 grid-cols-4 gap-3'}>
          <LabelBadge
            label={'Diphtérie'}
            value={enrollmentHealth?.diphtheria}
            color={tailwindColors.grey.lighter}
          />
          <LabelBadge
            label={'Tétanos'}
            value={enrollmentHealth?.tetanus}
            color={tailwindColors.grey.lighter}
          />
          <LabelBadge
            label={'Poliomyélite'}
            value={enrollmentHealth?.poliomyelitis}
            color={tailwindColors.grey.lighter}
          />
          <LabelBadge
            label={'OU DT Polio / Tétracoq'}
            value={enrollmentHealth?.dtPolioTetracoq}
            color={tailwindColors.grey.lighter}
          />
        </div>

        <Typography variant={'label'} className={'mt-2'}>
          Autres vaccinations (préciser): {valueRender(enrollmentHealth?.otherVaccinations)}
        </Typography>
      </InnerBox>

      <InnerBox
        title={'Vaccins recommandés'}
        className={'rounded-lg bg-white-hover border border-grey-light'}
      >
        <div className={'grid mobile:grid-cols-1 grid-cols-4 gap-3'}>
          <LabelBadge
            label={'Coqueluche'}
            value={enrollmentHealth?.pertussis}
            color={tailwindColors.grey.lighter}
          />
          <LabelBadge
            label={'Hépatite B'}
            value={enrollmentHealth?.hepatitisB}
            color={tailwindColors.grey.lighter}
          />
          <LabelBadge
            label={'Rubéole - Oreillons - Rougeole'}
            value={enrollmentHealth?.rubellaMumpsMeasles}
            color={tailwindColors.grey.lighter}
          />
          <LabelBadge
            label={'BCG'}
            value={enrollmentHealth?.bcg}
            color={tailwindColors.grey.lighter}
          />
          <LabelBadge
            label={'Haemophilus'}
            value={enrollmentHealth?.haemophilus}
            color={tailwindColors.grey.lighter}
          />
          <LabelBadge
            label={'Pneumocoque'}
            value={enrollmentHealth?.pneumococcus}
            color={tailwindColors.grey.lighter}
          />
          <LabelBadge
            label={'Méningocoque C'}
            value={enrollmentHealth?.meningococcusC}
            color={tailwindColors.grey.lighter}
          />
        </div>
      </InnerBox>

      <Typography variant={'sub-heading'}>Recommandations utiles des parents</Typography>

      <InnerBox className={'rounded-lg'}>
        <Typography variant={'label-semibold'}>
          Voici les informations de santé que vous nous avez communiquées lors de votre demande
          d'inscription :
        </Typography>

        <Typography variant={'label'} className={'mt-2'}>
          {valueRender(enrollmentHealth?.additionalRecommendations)}
        </Typography>
      </InnerBox>
    </div>
  )
}
